/**
 * WITHDRAWAL ESCROW SERVICE
 *
 * Comprehensive service for managing USDT commission withdrawals with escrow system.
 * Handles atomic transactions, audit trails, and email notifications.
 *
 * Features:
 * - Atomic escrow transactions (usdt_balance -> escrowed_amount)
 * - Complete audit trail in withdrawal_transactions table
 * - Email notifications for all stakeholders
 * - Rollback handling for failed operations
 * - Duplicate request prevention
 * - Balance validation
 * - AUTOMATED REMITANO INTEGRATION (2026-01-08)
 * - Automatic USDT transfers via Remitano API
 * - Admin-controlled 2FA input
 * - Comprehensive error handling
 * - Dry run mode for testing
 */

/**
 * SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
 */
import { supabase } from '../supabase';
import { resendEmailService } from '../resendEmailService';
import { remitanoWithdrawalService, type WithdrawalRequest as RemitanoRequest, type WithdrawalResult } from './remitanoWithdrawalService';
import { ensureSessionHeaders } from '../serverSession';
import { normalizeWithdrawalNetwork } from '../networkNormalization';

// SECURITY: Protected admin account IDs - withdrawals permanently blocked (client-side defense layer)
const PROTECTED_ADMIN_IDS = Object.freeze([1,4,89,102,103,104,105,106,107,111,113,115,123]);
function isProtectedAdmin(userId: number | string): boolean {
 const id = typeof userId === 'string' ? parseInt(userId, 10) : Number(userId);
 return !isNaN(id) && PROTECTED_ADMIN_IDS.includes(id);
}

// User-facing operations that don't require admin privileges
const USER_OPERATIONS = ['confirm_receipt', 'get_user_withdrawals'];

async function callEscrowAPI(operation: string, params: any = {}): Promise<any> {
 const sessionHeaders = await ensureSessionHeaders();
 // Route user operations to the user endpoint (no admin required)
 // Route admin operations to the admin endpoint (proven working)
 const endpoint = USER_OPERATIONS.includes(operation)
 ? '/api/user/withdrawal-actions'
 : '/api/admin/withdrawals';
 const response = await fetch(endpoint, {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({ operation, data: params }),
 });
 if (!response.ok) {
 const errorData = await response.json().catch(() => ({}));
 throw new Error(errorData.error || `API error: ${response.status}`);
 }
 const result = await response.json();
 // Both endpoints wrap response in { success, data }, unwrap to match old format
 return { data: result.data, success: result.success };
}

interface WithdrawalRequest {
 id?: string;
 user_id: number;
 withdrawal_amount: number;
 wallet_address: string;
 network: string;
 currency?: string;
 status?: string;
}

interface EscrowResult {
 success: boolean;
 withdrawalRequestId?: string;
 error?: string;
 availableBalance?: number;
 requiresVerification?: boolean;
 linkSent?: boolean;
	 expiresAt?: string | null;
}

interface ApprovalResult {
 success: boolean;
 error?: string;
 warning?: string;
 emailNotificationFailed?: boolean;
 status?: string;
 escrowCompleted?: boolean;
}

interface ConfirmationResult {
 success: boolean;
 error?: string;
}

interface AutomatedApprovalResult {
 success: boolean;
 transactionId?: string;
 remitanoTransactionId?: string;
 error?: string;
 errorCode?: string;
 retryable?: boolean;
 dryRun?: boolean;
}

export class WithdrawalEscrowService {

 /**
 * Submit withdrawal request with atomic escrow transaction
 *
 * SECURITY: Uses the authenticated supabase client (not service role) to ensure
 * the RPC function can validate auth.uid() matches the user_id.
 * This prevents attackers from submitting withdrawals for other users.
 */
 async submitWithdrawalRequest(request: WithdrawalRequest): Promise<EscrowResult> {
	 const { user_id, withdrawal_amount, wallet_address, network, currency = 'USDT' } = request;
	 const normalizedNetwork = normalizeWithdrawalNetwork(network);

 // SECURITY: Block withdrawal requests for protected admin accounts (client-side defense layer)
 if (isProtectedAdmin(user_id)) {
 console.error(`[SECURITY BLOCK] Withdrawal request blocked for protected admin account ${user_id}`);
 return { success: false, error: 'SECURITY: Withdrawal operations are permanently blocked for admin/system accounts.' };
 }

 // SYSTEM SETTING: Check if withdrawals are enabled globally
 try {
 const settingCheck = await fetch('/api/user/system-settings', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
 body: JSON.stringify({ operation: 'check_withdrawals_enabled' })
 });
 const settingResult = await settingCheck.json();
 if (settingResult.success && !settingResult.withdrawalsEnabled) {
 return { success: false, error: 'Withdrawals are temporarily disabled by the administrator. Please try again later.' };
 }
 } catch (settingErr) {
 console.warn('[ESCROW] Could not check withdrawal setting, proceeding:', settingErr);
 }

 // SECURITY (v4.0.45): Client-side locked wallet pre-check (defense in depth)
 // The RPC function enforces this at the database level — this is an early warning only
 try {
 const walletCheckHeaders = await ensureSessionHeaders();
 const walletCheckRes = await fetch('/api/user/locked-wallets', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...walletCheckHeaders },
 body: JSON.stringify({ operation: 'get_locked_wallets' })
 });
 const walletCheckData = await walletCheckRes.json();
 if (walletCheckData.success) {
 if (walletCheckData.wallets?.length > 0) {
 // Map network to locked wallet key
 const NETWORK_MAP: Record<string, string> = {
 'USDT-TRC20': 'TRC20', 'USDT-ERC20': 'Ethereum', 'USDT-BEP20': 'BSC', 'USDT-POL': 'Polygon',
 'TRON': 'TRON', 'TRC20': 'TRC20', 'ETH': 'Ethereum', 'BSC': 'BSC', 'POL': 'Polygon',
 'Ethereum': 'Ethereum', 'Polygon': 'Polygon'
 };
	 const lockedKey = NETWORK_MAP[network] || normalizedNetwork;
 // v4.7.18: Both centralized AND decentralized locked wallets are valid for USDT withdrawals.
 // Only NFT delivery requires decentralized (handled in NFT-specific paths). Match against ANY active wallet on the network.
 const networkMatches = walletCheckData.wallets.filter((w: any) => normalizeWithdrawalNetwork(w.network) === normalizeWithdrawalNetwork(lockedKey) && w.is_active);
 if (networkMatches.length > 0) {
 // Case-insensitive comparison for EVM addresses, case-sensitive for TRON
 const isTron = lockedKey === 'TRON' || lockedKey === 'TRC20';
 const submittedAddr = isTron ? wallet_address : wallet_address.toLowerCase();
 const isMatch = networkMatches.some((m: any) => {
 const storedAddr = isTron ? m.wallet_address : m.wallet_address.toLowerCase();
 return storedAddr === submittedAddr;
 });
 if (!isMatch) {
 const expectedAddrs = networkMatches.map((m: any) => m.wallet_address).join(', ');
 console.error(`[SECURITY BLOCK] Wallet address mismatch! Expected one of: ${expectedAddrs}, Got: ${wallet_address}`);
 return { success: false, error: 'Withdrawal rejected: wallet address does not match any of your registered wallets for this network.' };
 }
 } else {
 // User has locked wallets but not for this network
 return { success: false, error: 'No registered wallet address found for this network. Please register a wallet address first.' };
 }
 } else {
 // SECURITY (v4.0.54): User has ZERO locked wallets — always block ALL users
 // The dashboard blocking overlay forces registration for ALL users (no balance threshold)
 // The RPC also blocks ALL users without locked wallets at the database level
 console.error(`[SECURITY BLOCK] User ${user_id} has no locked wallets — blocking withdrawal of $${withdrawal_amount}`);
 return { success: false, error: 'Wallet registration required. Please register a locked BSC withdrawal wallet in your dashboard before requesting a withdrawal.' };
 }
 }
 } catch (walletCheckErr) {
 console.warn('[ESCROW] Locked wallet pre-check failed (RPC will enforce):', walletCheckErr);
 }

 try {
 console.log(`[ESCROW] Starting withdrawal request for User ${user_id}: $${withdrawal_amount}`);

 // SECURITY: Route through server-side API which validates session auth
 // The API calls begin_withdrawal_escrow_service RPC with service role
 // User identity is verified from server-side session (not client auth.uid())
 const sessionHeaders = await ensureSessionHeaders();
 const apiResponse = await fetch('/api/user/withdrawal-actions', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({
 operation: 'submit_withdrawal',
 data: {
 withdrawal_amount,
 wallet_address,
	 network: normalizedNetwork,
 currency
 }
 })
 });

 const apiResult = await apiResponse.json();

 if (!apiResponse.ok || !apiResult.success) {
 const errorMsg = apiResult.error || 'Withdrawal processing failed';
 console.error('[ESCROW] Validation failed:', errorMsg);
 return {
 success: false,
 error: errorMsg,
 availableBalance: apiResult.available_balance
 };
 }

 const result = apiResult.data || apiResult;

 console.log(`[ESCROW] Withdrawal request created: ${result.withdrawal_id}`);
 console.log(`[ESCROW] Fee: $${result.network_fee}, Total deducted: $${result.total_deducted}, Remaining: $${result.remaining_balance}`);

 // Handle magic link verification flow (no PIN - only magic link)
 // SECURITY (v2.9.12): Only pass withdrawalId — token is looked up server-side from DB
 if (result.requires_verification && result.withdrawal_id) {
 console.log(`[ESCROW] Triggering server-side verification email for withdrawal ${result.withdrawal_id}`);
	 let expiresAt = result.expires_at || result.expiresAt || null;

 // Send magic link verification email via API — session headers authenticate the user
 // The token is NEVER sent from the client; the server looks it up from the DB
 let linkSent = false;
 try {
 const sessionHeaders = await ensureSessionHeaders();
 const emailResponse = await fetch('/api/send-withdrawal-verification', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({
 withdrawalId: result.withdrawal_id
 })
 });

 if (!emailResponse.ok) {
 let emailError;
 try { emailError = await emailResponse.json(); } catch { emailError = { error: `HTTP ${emailResponse.status}` }; }
 console.error('[ESCROW] Failed to send verification email:', emailResponse.status, emailError);
 } else {
 const emailResult = await emailResponse.json();
 console.log(`[ESCROW] Magic link verification email sent (token never exposed to client), emailId: ${emailResult.emailId}`);
 linkSent = true;
	 expiresAt = emailResult.expiresAt || expiresAt;
 }
 } catch (emailError) {
 console.error('[ESCROW] Network error sending verification email:', emailError);
 }

 return {
 success: true,
 withdrawalRequestId: result.withdrawal_id,
 requiresVerification: true,
 linkSent,
	 expiresAt,
 emailSentTo: result.email || '(server-side)'
 } as any;
 }

 // Legacy flow (shouldn't happen with new RPC, but keep for safety)
 await this.sendWithdrawalRequestEmails(user_id, withdrawal_amount, wallet_address, network, result.withdrawal_id);

 return {
 success: true,
 withdrawalRequestId: result.withdrawal_id
 };

 } catch (error) {
 console.error('[ESCROW] Unexpected error:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Submit share withdrawal request with atomic escrow transaction
 *
 * SECURITY: Uses the authenticated supabase client (not service role) to ensure
 * the RPC function can validate auth.uid() matches the user_id.
 */
 async submitShareWithdrawalRequest(userId: number, shares: number): Promise<EscrowResult> {
 // SYSTEM SETTING: Check if withdrawals are enabled globally
 try {
 const settingCheck = await fetch('/api/user/system-settings', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
 body: JSON.stringify({ operation: 'check_withdrawals_enabled' })
 });
 const settingResult = await settingCheck.json();
 if (settingResult.success && !settingResult.withdrawalsEnabled) {
 return { success: false, error: 'Withdrawals are temporarily disabled by the administrator. Please try again later.' };
 }
 } catch (settingErr) {
 console.warn('[ESCROW] Could not check withdrawal setting, proceeding:', settingErr);
 }

 try {
 console.log(`[ESCROW] Starting share withdrawal request for User ${userId}: ${shares} shares`);

 // SECURITY: Use authenticated client (not service role) so auth.uid() is validated in RPC
 const { data: txResult, error: txError } = await supabase.rpc('begin_share_withdrawal_escrow', {
 p_user_id: userId,
 p_shares: shares
 });

 if (txError) {
 console.error('[ESCROW] Share transaction failed:', txError);
 return { success: false, error: txError.message };
 }

 const result = txResult as any;

 if (!result.success) {
 console.error('[ESCROW] Share validation failed:', result.error);
 return {
 success: false,
 error: result.error
 };
 }

 console.log(`[ESCROW] Share withdrawal request created: ${result.withdrawal_id}`);
 console.log(`[ESCROW] Escrowed: ${result.escrowed_shares} shares, Remaining: ${result.remaining_shares} shares`);

 return {
 success: true,
 withdrawalRequestId: result.withdrawal_id
 };

 } catch (error) {
 console.error('[ESCROW] Unexpected error:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Admin completes share withdrawal (directly to completed, no user confirmation needed)
 */
 async completeShareWithdrawal(
 withdrawalId: string,
 adminId: number,
 adminNotes?: string
 ): Promise<ApprovalResult> {
 try {
 console.log(`‍ [ADMIN] Completing share withdrawal ${withdrawalId}`);

 const { data: rpcResult } = await callEscrowAPI('complete_share_withdrawal_rpc', {
 withdrawalId, adminId, adminNotes
 });

 const result = rpcResult as any;
 if (!result.success) {
 throw new Error(result.error || 'Failed to complete share withdrawal');
 }

 console.log(`[ADMIN] Share withdrawal completed: ${withdrawalId}, shares: ${result.completed_shares}`);
 return { success: true };

 } catch (error) {
 console.error('[ADMIN] Share completion failed:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Admin rejects share withdrawal request
 */
 async rejectShareWithdrawal(
 withdrawalId: string,
 adminId: number,
 rejectionReason: string
 ): Promise<ApprovalResult> {
 try {
 console.log(`‍ [ADMIN] Rejecting share withdrawal ${withdrawalId}`);

 const { data: rpcResult } = await callEscrowAPI('reject_share_withdrawal_rpc', {
 withdrawalId, adminId, rejectionReason
 });

 const result = rpcResult as any;
 if (!result.success) {
 throw new Error(result.error || 'Failed to reject share withdrawal');
 }

 console.log(`[ADMIN] Share withdrawal rejected: ${withdrawalId}, shares returned: ${result.returned_shares}`);
 return { success: true };

 } catch (error) {
 console.error('[ADMIN] Share rejection failed:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Admin approves withdrawal and uploads proof
 */
 async approveWithdrawal(
 withdrawalId: string,
 adminId: number,
 proofOfPaymentUrl: string,
 transactionHash: string,
 adminNotes?: string
 ): Promise<ApprovalResult> {
 try {
 console.log(`‍ [ADMIN] Approving withdrawal ${withdrawalId}`);

 // Update withdrawal request via API
 const { data: withdrawal } = await callEscrowAPI('approve_withdrawal', {
 withdrawalId, adminId, proofOfPaymentUrl, transactionHash, adminNotes
 });
 const escrowCompleted = withdrawal?.escrow_completed === true || withdrawal?.status === 'completed' || withdrawal?.rpcResult?.success === true;

 // Create audit record only for legacy/non-completed approval responses. Under
 // Option A the API/RPC completion path is authoritative and already records
 // completion; adding a client-side pending→approved row afterward creates an
 // out-of-order audit trail.
 if (!escrowCompleted) {
 await this.createAuditRecord(withdrawalId, withdrawal.user_id, 'approved', 'pending', 'approved', withdrawal.withdrawal_amount, adminId, adminNotes);
 }

 // Send email to user with proof. This is important notification work, but it
 // must never roll back a withdrawal after escrow has already completed.
 try {
 await this.sendProofOfPaymentEmail(withdrawal);
 } catch (emailError) {
 const warning = `Withdrawal completed, but proof email failed: ${emailError instanceof Error ? emailError.message : 'Unknown error'}`;
 if (escrowCompleted) {
 console.error('[ADMIN] Proof email failed after escrow completion; NOT rolling back completed ledger state.', emailError);
 return {
 success: true,
 warning,
 emailNotificationFailed: true,
 status: withdrawal?.status,
 escrowCompleted: true
 };
 }

 // Legacy fallback: only pre-completion approval responses may be rolled back.
 console.error('[ADMIN] CRITICAL: Email failed before escrow completion, rolling back approval...');

 try {
 await callEscrowAPI('rollback_approval', {
 withdrawalId,
 errorMessage: emailError instanceof Error ? emailError.message : 'Unknown error'
 });

 console.log('[ADMIN] Rollback successful - withdrawal reverted to pending');
 } catch (rollbackError) {
 console.error('[ADMIN] CRITICAL: Rollback failed!', rollbackError);
 }

 // Re-throw the email error so the UI knows it failed
 throw emailError;
 }

 console.log(`[ADMIN] Withdrawal approved: ${withdrawalId}`);
 return { success: true, status: withdrawal?.status, escrowCompleted };

 } catch (error) {
 console.error('[ADMIN] Approval failed:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * AUTOMATED APPROVAL: Process withdrawal via Remitano API
 *
 * This method:
 * 1. Validates the withdrawal request
 * 2. Calls Remitano API to initiate transfer
 * 3. Updates withdrawal record with transaction details
 * 4. Sends email notifications
 *
 * @param withdrawalId - The withdrawal request ID
 * @param adminId - The admin processing the withdrawal
 * @param twoFactorCode - 2FA code for Remitano (optional if TOTP configured)
 * @param adminNotes - Optional notes from admin
 */
 async approveWithdrawalAutomated(
 withdrawalId: string,
 adminId: number,
 twoFactorCode?: string,
 adminNotes?: string
 ): Promise<AutomatedApprovalResult> {
 try {
 console.log(`[AUTO] Starting automated withdrawal ${withdrawalId}`);

 // Get withdrawal details via API
 const { data: withdrawal } = await callEscrowAPI('get_withdrawal_details', { withdrawalId });

 if (!withdrawal) {
 return { success: false, error: 'Withdrawal not found' };
 }

 if (withdrawal.status !== 'pending') {
 return { success: false, error: `Cannot process withdrawal with status: ${withdrawal.status}` };
 }

 // Prepare Remitano request
 const remitanoRequest: RemitanoRequest = {
 withdrawalId: withdrawalId,
 amount: withdrawal.withdrawal_amount,
 walletAddress: withdrawal.wallet_address,
 network: withdrawal.network as 'TRC20' | 'ERC20' | 'BEP20',
 currency: withdrawal.currency || 'USDT',
 adminId: adminId,
 twoFactorCode: twoFactorCode
 };

 // Process via Remitano
 const result: WithdrawalResult = await remitanoWithdrawalService.processWithdrawal(remitanoRequest);

 if (!result.success) {
 console.error(`[AUTO] Remitano transfer failed:`, result.error);

 // Update withdrawal with error (but don't change status - admin can retry)
 await callEscrowAPI('update_withdrawal_automation_error', {
 withdrawalId,
 errorCode: result.error?.code,
 errorMessage: result.error?.message
 });

 return {
 success: false,
 error: result.error?.message || 'Remitano transfer failed',
 errorCode: result.error?.code,
 retryable: result.error?.retryable
 };
 }

 // Success! Update withdrawal to approved status via API
 try {
 await callEscrowAPI('update_withdrawal_automation_success', {
 withdrawalId,
 transactionHash: result.transactionId,
 remitanoTransactionId: result.remitanoTransactionId,
 adminId,
 adminNotes
 });
 } catch (updateError) {
 console.error('[AUTO] Failed to update withdrawal record:', updateError);
 // Transfer succeeded but DB update failed - log for manual review
 return {
 success: true,
 transactionId: result.transactionId,
 remitanoTransactionId: result.remitanoTransactionId,
 error: 'Transfer succeeded but database update failed - please review manually',
 dryRun: result.dryRun
 };
 }

 // Create audit record
 await this.createAuditRecord(
 withdrawalId,
 withdrawal.user_id,
 'automated_approval',
 'pending',
 'approved',
 withdrawal.withdrawal_amount,
 adminId,
 `Automated via Remitano. TX: ${result.transactionId || result.remitanoTransactionId}`
 );

 // Send email notification to user
 try {
 await this.sendAutomatedApprovalEmail(withdrawal, result.transactionId || result.remitanoTransactionId || '');
 } catch (emailError) {
 console.warn('[AUTO] Email notification failed (non-critical):', emailError);
 // Don't fail the operation for email errors
 }

 console.log(`[AUTO] Withdrawal ${withdrawalId} processed successfully via Remitano`);

 return {
 success: true,
 transactionId: result.transactionId,
 remitanoTransactionId: result.remitanoTransactionId,
 dryRun: result.dryRun
 };

 } catch (error) {
 console.error('[AUTO] Unexpected error:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Send automated approval email to user
 */
 private async sendAutomatedApprovalEmail(withdrawal: any, transactionId: string) {
 if (!withdrawal.users?.email) {
 console.warn('[AUTO] No email address for user');
 return;
 }

 try {
 // Use the existing proof of payment email flow
 const withdrawalWithProof = {
 ...withdrawal,
 transaction_hash: transactionId,
 proof_of_payment_url: `https://tronscan.org/#/transaction/${transactionId}` // Default to TronScan for TRC20
 };

 await this.sendProofOfPaymentEmail(withdrawalWithProof);
 } catch (error) {
 console.error('[AUTO] Failed to send automated approval email:', error);
 throw error;
 }
 }

 /**
 * Check if Remitano automation is available
 */
 isAutomationAvailable(): boolean {
 return remitanoWithdrawalService.isConfigured() || remitanoWithdrawalService.isDryRunMode();
 }

 /**
 * Get Remitano automation status
 */
 async getAutomationStatus(adminId: number): Promise<{
 configured: boolean;
 dryRun: boolean;
 healthy: boolean;
 message: string;
 remainingCalls: number;
 }> {
 const configured = remitanoWithdrawalService.isConfigured();
 const dryRun = remitanoWithdrawalService.isDryRunMode();
 const remainingCalls = remitanoWithdrawalService.getRemainingApiCalls(adminId);

 if (!configured && !dryRun) {
 return {
 configured: false,
 dryRun: false,
 healthy: false,
 message: 'Remitano API not configured',
 remainingCalls
 };
 }

 if (dryRun) {
 return {
 configured: true,
 dryRun: true,
 healthy: true,
 message: 'Dry run mode - no actual transfers',
 remainingCalls
 };
 }

 const healthCheck = await remitanoWithdrawalService.healthCheck(adminId);
 return {
 configured: true,
 dryRun: false,
 healthy: healthCheck.healthy,
 message: healthCheck.message,
 remainingCalls
 };
 }

 /**
 * Get automation statistics
 */
 async getAutomationStats() {
 return remitanoWithdrawalService.getAutomationStats();
 }

 /**
 * Admin rejects withdrawal request
 */
 async rejectWithdrawal(
 withdrawalId: string,
 adminId: number,
 rejectionReason: string
 ): Promise<ApprovalResult> {
 try {
 console.log(`‍ [ADMIN] Rejecting withdrawal ${withdrawalId}`);

 // Get withdrawal details + reject atomically via API
 const { data: withdrawal } = await callEscrowAPI('reject_withdrawal_rpc', {
 withdrawalId, adminId, rejectionReason
 });

 // Create audit record
 await this.createAuditRecord(withdrawalId, withdrawal.user_id, 'rejected', 'pending', 'rejected', withdrawal.withdrawal_amount, adminId, rejectionReason);

 // Send rejection email to user
 await this.sendRejectionEmail(withdrawal, rejectionReason);

 console.log(`[ADMIN] Withdrawal rejected: ${withdrawalId} - amount + $2 fee refunded to user`);
 return { success: true };

 } catch (error) {
 console.error('[ADMIN] Rejection failed:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * User confirms receipt of withdrawal
 */
 async confirmReceipt(withdrawalId: string, userId: number): Promise<ConfirmationResult> {
 try {
 console.log(`[USER] Confirming receipt for withdrawal ${withdrawalId}`);

 // Get withdrawal details + complete atomically via API
 const apiResponse = await callEscrowAPI('confirm_receipt', {
 withdrawalId, userId
 });

 // The API returns { success, data: { ...existingWd, rpcResult } }
 // callEscrowAPI unwraps to { success, data }, so rpcResult is inside data
 const existingWithdrawal = apiResponse.data;
 const rpcResult = existingWithdrawal?.rpcResult as any;

 if (rpcResult && !rpcResult.success) {
 throw new Error(rpcResult.error || 'Failed to complete withdrawal');
 }

 console.log(`[USER] Withdrawal completed via RPC: ${withdrawalId}, amount: $${rpcResult?.completed_amount}`);

 // Send completion emails (non-blocking - don't fail if email service unavailable)
 try {
 // Update the withdrawal object with completed status for email
 const completedWithdrawal = { ...existingWithdrawal, status: 'completed' };
 await this.sendCompletionEmails(completedWithdrawal);
 } catch (emailError) {
 console.warn('[USER] Could not send completion emails (non-critical):', emailError);
 // Don't throw - email failure shouldn't prevent confirmation success
 }

 return { success: true };

 } catch (error) {
 console.error('[USER] Confirmation failed:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Get pending withdrawals for admin dashboard
 * Status flow: pending -> approved -> completed (or rejected)
 */
 async getPendingWithdrawals() {
 const { data } = await callEscrowAPI('get_pending_withdrawals');
 return data;
 }

 /**
 * Get approved withdrawals awaiting user confirmation
 */
 async getApprovedWithdrawals() {
 const { data } = await callEscrowAPI('get_approved_withdrawals');
 return data;
 }

 /**
 * Get user's withdrawal history
 */
 async getUserWithdrawals(userId: number) {
 const { data } = await callEscrowAPI('get_user_withdrawals', { userId });
 if (Array.isArray(data)) return data;
 if (data && Array.isArray((data as any).withdrawals)) return (data as any).withdrawals;
 return [];
 }

 /**
 * Create audit trail record
 */
 private async createAuditRecord(
 withdrawalId: string,
 userId: number,
 actionType: string,
 oldStatus: string,
 newStatus: string,
 amount: number,
 adminId?: number,
 notes?: string
 ) {
 try {
 await callEscrowAPI('create_audit_record', {
 withdrawalId, userId, actionType, oldStatus, newStatus, amount, adminId, notes
 });
 } catch (error) {
 console.warn('[AUDIT] Failed to create audit record (non-fatal):', error);
 }
 }

 /**
 * Send withdrawal request emails (user confirmation + admin alert)
 */
 private async sendWithdrawalRequestEmails(userId: number, amount: number, walletAddress: string, network: string, withdrawalId: string) {
 try {
 // Get user details via API
 const { data: user } = await callEscrowAPI('get_user_for_email', { userId });

 if (!user) {
 console.error('[EMAIL] User not found');
 return;
 }

 // Send user confirmation email
 await resendEmailService.sendWithdrawalRequestNotification(
 user.email,
 user.username || user.full_name || 'User',
 amount,
 'USDT',
 walletAddress,
 network
 );

 // Send admin alert email
 await resendEmailService.sendAdminWithdrawalAlert(
 user.username || user.full_name || 'User',
 userId,
 amount,
 walletAddress,
 network,
 withdrawalId
 );

 } catch (error) {
 console.error('[EMAIL] Failed to send withdrawal request emails:', error);
 }
 }

 /**
 * Send proof of payment email to user via backend API
 * CRITICAL: This MUST succeed - if it fails, the entire approval should fail
 *
 * NOTE: Cannot call Resend API directly from browser due to CORS restrictions.
 * Must use backend API endpoint which is deployed as Vercel serverless function.
 */
 private async sendProofOfPaymentEmail(withdrawal: any) {
 console.log('[EMAIL] Attempting to send proof of payment email via backend API...');

 if (!withdrawal.users || !withdrawal.users.email) {
 throw new Error('CRITICAL: Cannot send email - user email not found in withdrawal data');
 }

 try {
 // Determine API base URL based on environment
 // In production (Vercel), use relative URLs to hit serverless functions
 // In development, use localhost backend
 const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
 const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

 // Only use localhost API for actual localhost - everything else uses relative URLs
 const API_BASE_URL = isLocalhost ? ((import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:8002') : '';

 console.log(`[EMAIL] Using API base URL: ${API_BASE_URL || '(relative)'}, hostname: ${hostname}`);

 // Prepare email data
 const emailData = {
 email: withdrawal.users.email,
 userName: withdrawal.users.username || withdrawal.users.full_name || 'User',
 amount: withdrawal.withdrawal_amount,
 transactionHash: withdrawal.transaction_hash,
 proofUrl: withdrawal.proof_of_payment_url || '',
 withdrawalId: withdrawal.id
 };

 console.log(`[EMAIL] Sending withdrawal proof notification to: ${withdrawal.users.email}`);

 // Call backend API endpoint (deployed as Vercel serverless function in production)
 const sessionHeaders = await ensureSessionHeaders();
 const response = await fetch(`${API_BASE_URL}/api/send-withdrawal-email`, {
 method: 'POST',
 headers: {
 'Content-Type': 'application/json',
 ...sessionHeaders,
 },
 body: JSON.stringify(emailData)
 });

 if (!response.ok) {
 const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
 throw new Error(`Backend API error: ${errorData.error || response.statusText}`);
 }

 const result = await response.json();
 console.log(`[EMAIL] Proof email sent successfully to ${withdrawal.users.email}. Email ID: ${result.emailId}`);
 } catch (error) {
 console.error('[EMAIL] CRITICAL: Failed to send proof email:', error);
 // RE-THROW the error so the approval fails
 throw new Error(`CRITICAL: Email notification failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
 }
 }

 /**
 * Send rejection email to user
 */
 private async sendRejectionEmail(withdrawal: any, rejectionReason: string) {
 try {
 if (!withdrawal?.users?.email) {
 console.warn('[EMAIL] Cannot send rejection email - user email not available');
 return;
 }
 await resendEmailService.sendWithdrawalRejectionNotification(
 withdrawal.users.email,
 withdrawal.users.username || withdrawal.users.full_name || 'User',
 withdrawal.withdrawal_amount,
 rejectionReason
 );
 } catch (error) {
 console.error('[EMAIL] Failed to send rejection email:', error);
 }
 }

 /**
 * Send completion emails (user + admin confirmation)
 * FIXED: Now uses centralized backend email API instead of frontend service
 */
 private async sendCompletionEmails(withdrawal: any) {
 try {
 if (!withdrawal?.users?.email) {
 console.warn('[EMAIL] Cannot send completion emails - user email not available');
 return;
 }

 console.log('[EMAIL] Sending withdrawal completion emails via centralized API...');

 // Send user completion email via backend API
 const completionSessionHeaders = await ensureSessionHeaders();
 const userEmailResponse = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...completionSessionHeaders },
 body: JSON.stringify({
 emailType: 'withdrawal_completion',
 data: {
 email: withdrawal.users.email,
 userName: withdrawal.users.username || withdrawal.users.full_name || 'User',
 amount: withdrawal.withdrawal_amount,
 transactionHash: withdrawal.transaction_hash
 }
 })
 });

 if (!userEmailResponse.ok) {
 const error = await userEmailResponse.json();
 console.error('[EMAIL] User completion email failed:', error);
 } else {
 console.log('[EMAIL] User completion email sent successfully');
 }

 // Send admin confirmation email via backend API
 const adminEmailResponse = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...completionSessionHeaders },
 body: JSON.stringify({
 emailType: 'withdrawal_admin_confirmation',
 data: {
 userName: withdrawal.users.username || withdrawal.users.full_name || 'User',
 userId: withdrawal.user_id,
 amount: withdrawal.withdrawal_amount,
 transactionHash: withdrawal.transaction_hash
 }
 })
 });

 if (!adminEmailResponse.ok) {
 const error = await adminEmailResponse.json();
 console.error('[EMAIL] Admin confirmation email failed:', error);
 } else {
 console.log('[EMAIL] Admin confirmation email sent successfully');
 }

 } catch (error) {
 console.error('[EMAIL] Failed to send completion emails:', error);
 // Don't throw - email failure shouldn't prevent confirmation success
 }
 }
}

export const withdrawalEscrowService = new WithdrawalEscrowService();
